import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Loader2,
  Package,
  DollarSign,
  Receipt,
  CheckCircle2,
  AlertTriangle,
  File,
  Eye,
} from "lucide-react";
import { realEstateDocumentsApi, documentsApi } from "../../services/api";
import { motion } from "framer-motion";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
}

// ─── Generator Card Config ──────────────────────────────────────────────────

const GENERATOR_CARDS = [
  {
    type: "closing-report",
    title: "Closing Report",
    description: "Complete closing report with cover page, TOC, and sections",
    icon: FileText,
    iconBg: "bg-cyan-50",
    iconColor: "text-cyan-600",
  },
  {
    type: "soa",
    title: "Statement of Adjustments",
    description: "Detailed statement of adjustments for the transaction",
    icon: DollarSign,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
  },
  {
    type: "account-statement",
    title: "Account Statement",
    description: "Trust account statement with all receipts and disbursements",
    icon: Receipt,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
  },
  {
    type: "package",
    title: "Closing Package",
    description: "Assemble all closing documents into one PDF package",
    icon: Package,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
];

// ─── Component ──────────────────────────────────────────────────────────────

const RealEstateDocumentsTab: React.FC<Props> = ({ matterId, reCase }) => {
  // Generation state
  const [generating, setGenerating] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState("");
  const [generateSuccess, setGenerateSuccess] = useState("");

  // Documents state
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // ─── Fetch matter documents on mount ────────────────────────────────────

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await documentsApi.list({ matterId });
        setDocuments(res.data?.data || res.data || []);
      } catch {
        // Silently fail  -  empty state will show
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchDocuments();
  }, [matterId]);

  // ─── Generate handler ───────────────────────────────────────────────────

  const handleGenerate = async (type: string) => {
    setGenerating(type);
    setGenerateError("");
    setGenerateSuccess("");

    try {
      let res;
      switch (type) {
        case "closing-report":
          res = await realEstateDocumentsApi.generateClosingReport(reCase._id);
          break;
        case "soa":
          res = await realEstateDocumentsApi.generateSoa(reCase._id);
          break;
        case "account-statement":
          res = await realEstateDocumentsApi.generateAccountStatement(reCase._id);
          break;
        case "package":
          res = await realEstateDocumentsApi.assemblePackage(reCase._id);
          break;
        default:
          throw new Error("Unknown document type");
      }

      // Download the blob
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      setGenerateSuccess(
        `${type.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} generated successfully`
      );
    } catch (err: any) {
      setGenerateError(
        err.response?.data?.error || "Failed to generate document"
      );
    } finally {
      setGenerating(null);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      {/* ── Section 1: Document Generation ──────────────────────────────── */}
      <div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">
          Generate Documents
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Auto-generate closing documents from case data
        </p>

        {/* Success banner */}
        {generateSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-emerald-700">{generateSuccess}</span>
          </div>
        )}

        {/* Error banner */}
        {generateError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{generateError}</span>
          </div>
        )}

        {/* Generator cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {GENERATOR_CARDS.map((card) => {
            const Icon = card.icon;
            const isGenerating = generating === card.type;

            return (
              <div
                key={card.type}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`h-10 w-10 rounded-xl ${card.iconBg} flex items-center justify-center`}
                  >
                    <Icon className={`h-5 w-5 ${card.iconColor}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">
                      {card.title}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {card.description}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleGenerate(card.type)}
                  disabled={isGenerating}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-colors hover:opacity-90"
                  style={{ backgroundColor: "#0F5132" }}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Generate PDF
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-3 italic">
          All auto-generated documents require lawyer review before use.
        </p>
      </div>

      {/* ── Section 2: Matter Documents ─────────────────────────────────── */}
      <div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">
          Matter Documents
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Documents uploaded to this matter
        </p>

        {loadingDocs ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <File className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No documents uploaded yet
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Name
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Type
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Uploaded
                  </th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc._id}
                    className="border-b border-slate-50 hover:bg-slate-50/50"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">
                          {doc.title || doc.originalFilename}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {doc.mimeType?.split("/")[1] || "file"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-500">
                      {new Date(doc.createdAt).toLocaleDateString("en-CA")}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <a
                        href={documentsApi.downloadUrl(doc.storageKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RealEstateDocumentsTab;
